import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, ModalFooter } from '../ui/modal';
import { Input, Select, Checkbox } from '../ui/form';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { useCreateDivisionUser, useUpdateDivisionUser, useDivisionUser } from '../../hooks/use-division-users';
import { useDivisionRoles } from '../../hooks/use-division-roles';
import { useAuth } from '../../hooks/use-auth';
import { CreateUserRequest, UpdateUserRequest, UserDto, CreateUserRoleRequest, ALL_SYSTEM_ROLES } from '../../types';
import { X, Plus } from 'lucide-react';

interface UserFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  divisionId: string;
  mode: 'create' | 'edit';
  userId?: string;
}

interface FormData {
  email: string;
  firstName: string;
  lastName: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  active: boolean;
  createInOkta: boolean;
}

export function UserFormModal({ 
  isOpen, 
  onClose, 
  divisionId,
  mode,
  userId
}: UserFormModalProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<CreateUserRoleRequest[]>([]);
  const [currentRole, setCurrentRole] = useState<string>('');
  const [selectedSystemRoles, setSelectedSystemRoles] = useState<string[]>([]);
  const { isAdmin } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
  } = useForm<FormData>({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      active: true,
      createInOkta: false,
    },
  });

  // Get existing user data for edit mode
  const { data: existingUser } = useDivisionUser(
    divisionId,
    userId || '',
    mode === 'edit' && !!userId
  );

  // Get division roles for the dropdown
  const { data: divisionRolesResponse, isLoading: isLoadingRoles } = useDivisionRoles(divisionId);
  const divisionRoles = divisionRolesResponse?.roles || [];

  const createUserMutation = useCreateDivisionUser(divisionId);
  const updateUserMutation = useUpdateDivisionUser(divisionId);

  // Populate form with existing user data in edit mode
  useEffect(() => {
    if (mode === 'edit' && existingUser) {
      setValue('email', existingUser.email);
      setValue('firstName', existingUser.firstName);
      setValue('lastName', existingUser.lastName);
      setValue('address', existingUser.address || '');
      setValue('city', existingUser.city || '');
      setValue('state', existingUser.state || '');
      setValue('zipCode', existingUser.zipCode || '');
      setValue('active', existingUser.active);
      setSelectedSystemRoles(existingUser.systemRoles || []);
      // Set the selected roles from the user's division roles array
      if (existingUser.divisionRoles && existingUser.divisionRoles.length > 0) {
        setSelectedRoles(existingUser.divisionRoles.map(dr => ({
          divisionId: dr.divisionId,
          role: dr.role,
        })));
      }
    } else if (mode === 'create') {
      // Set a default role for new users
      const defaultRole = getDefaultRole();
      if (defaultRole) {
        setSelectedRoles([{ divisionId, role: defaultRole }]);
      }
    }
  }, [mode, existingUser, divisionRoles, setValue, divisionId]);

  // Reset currentRole when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCurrentRole('');
    }
  }, [isOpen]);

  const onSubmit = async (data: FormData) => {
    try {
      setSubmitError(null);

      // Validate that at least one role is selected
      if (selectedRoles.length === 0) {
        setSubmitError('Please select at least one role for the user');
        return;
      }

      if (mode === 'create') {
        const createRequest: CreateUserRequest = {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          address: data.address,
          city: data.city,
          state: data.state,
          zipCode: data.zipCode,
          divisionRoles: selectedRoles,
          createInOkta: data.createInOkta,
          systemRoles: isAdmin ? selectedSystemRoles : undefined,
        };

        await createUserMutation.mutateAsync(createRequest);
      } else if (userId) {
        const updateRequest: UpdateUserRequest = {
          email: data.email,
          firstName: data.firstName,
          lastName: data.lastName,
          address: data.address,
          city: data.city,
          state: data.state,
          zipCode: data.zipCode,
          active: data.active,
          divisionRoles: selectedRoles,
          systemRoles: isAdmin ? selectedSystemRoles : undefined,
        };

        await updateUserMutation.mutateAsync({ userId, userData: updateRequest });
      }

      handleClose();
    } catch (error: any) {
      setSubmitError(error?.apiError?.message || error?.message || 'Failed to save user');
    }
  };

  const handleClose = () => {
    reset();
    setSubmitError(null);
    setSelectedRoles([]);
    setCurrentRole('');
    setSelectedSystemRoles([]);
    onClose();
  };

  const roleOptions = [
    { value: 'Shopper', label: 'Shopper' },
    { value: 'Approver', label: 'Approver' },
    { value: 'WorkflowAdmin', label: 'Workflow Admin' },
    { value: 'DivisionAdmin', label: 'Division Admin' },
    { value: 'Admin', label: 'Admin' },
  ];

  // Use division-defined roles if available, otherwise fallback to default roles
  const availableRoles = divisionRoles.length > 0 
    ? divisionRoles.map(role => ({ value: role.name, label: role.name }))
    : roleOptions;

  // Helper function to get a good default role
  const getDefaultRole = () => {
    if (divisionRoles.length > 0) {
      // Prefer 'Shopper' if available, otherwise take the first role
      return divisionRoles.find(r => r.name === 'Shopper')?.name || divisionRoles[0]?.name;
    }
    return 'Shopper'; // Fallback to default
  };

  // Add a role to the selected roles
  const handleAddRole = () => {
    if (!currentRole) {
      return;
    }

    // Check if this role is already added for this division
    const alreadyExists = selectedRoles.some(
      sr => sr.divisionId === divisionId && sr.role === currentRole
    );

    if (alreadyExists) {
      setSubmitError('This role is already assigned');
      return;
    }

    setSelectedRoles([...selectedRoles, { divisionId, role: currentRole }]);
    setCurrentRole('');
    setSubmitError(null);
  };

  // Remove a role from the selected roles
  const handleRemoveRole = (index: number) => {
    setSelectedRoles(selectedRoles.filter((_, i) => i !== index));
  };

  const toggleSystemRole = (role: string) => {
    setSelectedSystemRoles(prev =>
      prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={mode === 'create' ? 'Add New User' : 'Edit User'}
      size="xl"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {submitError && (
          <div className="rounded-md bg-red-50 dark:bg-red-950 p-4">
            <div className="text-sm text-red-700 dark:text-red-400">{submitError}</div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Email */}
          <div className="md:col-span-2">
            <Input
              label="Email Address"
              type="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              error={errors.email?.message}
            />
          </div>

          {/* First Name */}
          <Input
            label="First Name"
            {...register('firstName', {
              required: 'First name is required',
            })}
            error={errors.firstName?.message}
          />

          {/* Last Name */}
          <Input
            label="Last Name"
            {...register('lastName', {
              required: 'Last name is required',
            })}
            error={errors.lastName?.message}
          />

          {/* Address */}
          <div className="md:col-span-2">
            <Input
              label="Address (Optional)"
              {...register('address')}
              error={errors.address?.message}
            />
          </div>

          {/* City */}
          <Input
            label="City (Optional)"
            {...register('city')}
            error={errors.city?.message}
          />

          {/* State */}
          <Input
            label="State (Optional)"
            {...register('state')}
            error={errors.state?.message}
          />

          {/* ZIP Code */}
          <Input
            label="ZIP Code (Optional)"
            {...register('zipCode')}
            error={errors.zipCode?.message}
          />
        </div>

        {mode === 'create' && (
          <Checkbox
            label="Also create this user in Okta"
            helperText="Creates (or adds to the configured group) a matching Okta account so the user can sign in."
            {...register('createInOkta')}
          />
        )}

        {/* Division Roles */}
        <div className="space-y-3">
          <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200">
            Division Roles
          </label>
          
          {/* Display selected roles */}
          {selectedRoles.length > 0 && (
            <div className="flex flex-wrap gap-2 p-3 bg-secondary-50 dark:bg-secondary-900 rounded-lg border border-secondary-200 dark:border-secondary-700">
              {selectedRoles.map((selectedRole, index) => (
                <Badge key={index} variant="info" className="flex items-center gap-1">
                  <span>{selectedRole.role}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveRole(index)}
                    className="ml-1 hover:bg-blue-700 rounded-full p-0.5"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}

          {/* Add role selector */}
          <div className="flex gap-2">
            <div className="flex-1">
              <select
                value={currentRole}
                onChange={(e) => setCurrentRole(e.target.value)}
                disabled={isLoadingRoles}
                className="block w-full rounded-md border-secondary-300 dark:border-secondary-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm bg-white dark:bg-secondary-900 text-secondary-900 dark:text-secondary-100"
              >
                {isLoadingRoles ? (
                  <option value="">Loading roles...</option>
                ) : availableRoles.length === 0 ? (
                  <option value="">No roles available</option>
                ) : (
                  <>
                    <option value="">Select a role to add...</option>
                    {availableRoles.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </div>
            <Button
              type="button"
              onClick={handleAddRole}
              disabled={!currentRole || isLoadingRoles}
              size="sm"
              variant="outline"
              className="flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              Add Role
            </Button>
          </div>

          {divisionRoles.length > 0 && (
            <p className="text-xs text-secondary-500 dark:text-secondary-400">
              Using division-specific roles
            </p>
          )}

          {selectedRoles.length === 0 && (
            <p className="text-xs text-red-600 dark:text-red-400">
              At least one role must be assigned
            </p>
          )}
        </div>

        {/* Active Status */}
        <Checkbox
          label="Active User"
          {...register('active')}
          helperText="Active users can sign in and access the system"
        />

        {/* System Roles (global, not division-scoped) - admin-only */}
        {isAdmin && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-secondary-700 dark:text-secondary-200">
              System Roles
            </label>
            <p className="text-xs text-secondary-500 dark:text-secondary-400">
              Global roles that apply across all divisions, replacing the old Okta-group-based roles.
            </p>
            <div className="flex flex-wrap gap-4">
              {ALL_SYSTEM_ROLES.map((role) => (
                <label key={role} className="flex items-center gap-2 text-sm text-secondary-700 dark:text-secondary-200">
                  <input
                    type="checkbox"
                    checked={selectedSystemRoles.includes(role)}
                    onChange={() => toggleSystemRole(role)}
                    className="rounded border-secondary-300 dark:border-secondary-600 text-primary-600 focus:ring-primary-500"
                  />
                  {role}
                </label>
              ))}
            </div>
          </div>
        )}
      </form>

      <ModalFooter>
        <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          disabled={isSubmitting}
        >
          {isSubmitting 
            ? (mode === 'create' ? 'Creating...' : 'Updating...') 
            : (mode === 'create' ? 'Create User' : 'Update User')
          }
        </Button>
      </ModalFooter>
    </Modal>
  );
}
